import { drawGate } from '@urbanrats/domain';
import { useEffect, useRef, useState } from 'react';
import type { GameSelection, Money, RoundSnapshot } from '@urbanrats/contracts';
import type { ClientGateways } from '../gateway/types.ts';
import { useI18n, type MessageKey } from '../i18n/index.ts';
import { fmtAtomic } from '../lib/format.ts';
import type { RoundOutcome } from '../useGame.ts';
import { introFrame, locationLines, type IntroFrame, type IntroTiming, type LonLat } from '../lib/introTimeline.ts';
import { Globe } from './Globe.tsx';
import { sounds } from '../sound.ts';

/**
 * LIVE round stage: the round's camera relayed by our server, with what the server's live counter saw
 * drawn on it — a coloured box per tracked vehicle, the locked counting line, and "+1" where a vehicle
 * was counted. Boxes and crossings come with each video segment (a JSON sidecar of exactly the segment
 * that was counted), so picture and count cannot disagree. Nothing is detected or counted in the browser.
 */
type Box = [id: number, x1: number, y1: number, x2: number, y2: number, cls: string];
interface Crossing {
  eventId: string;
  direction: string;
  vehicleClass: string;
  wallMs: number;
  seq: number;
  offsetMs: number;
  at: [number, number] | null;
}
interface Sidecar {
  seq: number;
  startMs: number;
  durationMs: number;
  frames: [offsetMs: number, boxes: Box[]][];
  crossings: Crossing[];
}
interface Seg extends Sidecar {
  /**
   * The player's fragment: its `start`/`duration` are the segment's position on the video timeline. Read
   * when drawing, not copied — hls.js corrects them to the real timestamps once the fragment is parsed.
   */
  frag: { start: number; duration: number };
}
interface Hit {
  eventId: string;
  /** Segment and offset of the crossing (its video time follows from the segment's fragment) and its capture time. */
  seq: number;
  offsetMs: number;
  /** Video time when the crossing arrived, used until its own segment is known. */
  fallbackT: number;
  wallMs: number;
  counted: boolean;
  at: [number, number] | null;
}

export interface PictureClock {
  /** Capture time of the picture on screen (ms), null while nothing plays. */
  wallMs: number | null;
  /** How far the picture is behind the server clock (ms). */
  behindMs: number | null;
}

/** Round intro: the globe turns to the round's country (about 5 s) while the relay loads underneath. */
const INTRO: IntroTiming = { rotateStart: 200, countryVisible: 1400, markerAt: 2400, infoAt: 1500, zoomAccelAt: 4000, transitionAt: 4000, videoAt: 4700, playingAt: 5200 };
const EARTH_VIEW: LonLat = { lon: 10, lat: 25 };
/** Seconds of picture kept buffered behind the newest counted segment (see the player setup). */
const CUSHION_S = 3.5;

const colour = (id: number) => `hsl(${(id * 47) % 360} 95% 62%)`;
const POP_S = 1.3;

export function LiveRelayStage({
  round,
  gw,
  outcome = null,
  reducedMotion = false,
  soundOn = false,
  onLiveCount,
  onPicture,
  onIntro,
}: {
  round: RoundSnapshot;
  gw: ClientGateways;
  outcome?: RoundOutcome | null;
  reducedMotion?: boolean;
  /** Sound cues (countdown, start, +1, end); the player's mute setting. */
  soundOn?: boolean;
  onLiveCount?: (n: number | null) => void;
  onPicture?: (c: PictureClock) => void;
  /** The globe intro runs (true) / ended (false): the new round's numbers appear with the map. */
  onIntro?: (playing: boolean) => void;
}) {
  const { t, intl } = useI18n();
  const m = round.manifest;
  const cam = m.camera!;
  const lines = locationLines(cam);
  const video = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const hlsRef = useRef<import('hls.js').default | null>(null);
  const segs = useRef(new Map<number, Seg>());
  const hits = useRef(new Map<string, Hit>());
  const earlier = useRef<number[]>([]);
  const [relay, setRelay] = useState<{ url: string; key: number; segS: number } | null>(null);
  const hasRelay = useRef(false);
  const [state, setState] = useState<'LOADING' | 'OK' | 'RETRYING'>('LOADING');
  // Seconds until this round's window starts on screen (null when it has started or nothing plays).
  const [startsIn, setStartsIn] = useState<number | null>(null);
  const [stale, setStale] = useState(false);
  // Countdown ticks for the last ten seconds before the counted minute.
  useEffect(() => {
    if (soundOn && startsIn !== null && startsIn > 0 && startsIn <= 10) sounds.countdown(startsIn);
  }, [startsIn, soundOn]);
  // Outro figures of the settled round (own operator and currency only).
  const [summary, setSummary] = useState<{ id: string; players: number; winners: number; totalPaid: Money } | null>(null);
  useEffect(() => {
    if (round.state !== 'SETTLED' || !gw.media.getRoundSummary) return;
    let live = true;
    const id = round.gameRoundId;
    void gw.media.getRoundSummary(id).then((x) => live && x && setSummary({ id, players: x.players, winners: x.winners, totalPaid: x.totalPaid }), () => undefined);
    return () => {
      live = false;
    };
  }, [gw, round.gameRoundId, round.state]);
  // The window of this round has ended on screen (the place is named only then).
  const [endedRound, setEndedRound] = useState<string | null>(null);
  const ended = endedRound === round.gameRoundId;

  // Intro on every new round that starts while we watch: the globe turns from the previous camera to this
  // round's country. Only the country is known (and shown) until the round is over.
  const [intro, setIntro] = useState<IntroFrame | null>(null);
  const onIntroRef = useRef(onIntro);
  useEffect(() => {
    onIntroRef.current = onIntro;
  });
  const lastView = useRef<LonLat>(EARTH_VIEW);
  const lat = cam.latitude;
  const lon = cam.longitude;
  const fresh = round.state === 'SCHEDULED' || round.state === 'BETTING_OPEN';
  useEffect(() => {
    if (lat === null || lon === null) return;
    const to = { lon, lat };
    const from = lastView.current;
    lastView.current = to;
    if (!fresh || reducedMotion) return;
    const t0 = performance.now();
    let raf = 0;
    // The new round's numbers appear when the globe has landed on the country (a timer, not the animation
    // frame: frames pause in a hidden tab).
    onIntroRef.current?.(true);
    const landed = setTimeout(() => onIntroRef.current?.(false), INTRO.markerAt);
    const tick = () => {
      const t = performance.now() - t0;
      if (t >= INTRO.playingAt) {
        setIntro(null);
        return;
      }
      setIntro(introFrame(t, from, to, INTRO));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(landed);
      setIntro(null);
      onIntroRef.current?.(false);
    };
    // A new round starts the intro; state changes of the same round do not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round.gameRoundId]);
  // Latest round and callbacks for the draw loop (which must not restart on every render).
  const roundRef = useRef(round);
  const cbs = useRef({ onLiveCount, onPicture, soundOn, reducedMotion });
  useEffect(() => {
    roundRef.current = round;
    cbs.current = { onLiveCount, onPicture, soundOn, reducedMotion };
  });

  // The relay of this round's camera (a new signed link per round; re-requested on failure). Re-read on
  // every state change so a late joiner's count starts from what was already counted in the window.
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    void gw.media.getLiveRelay?.(round.gameRoundId).then(
      (r) => {
        if (!live) return;
        if (!r) {
          // No answer: keep a relay that is already playing; only a stage without one asks again.
          if (!hasRelay.current) timer = setTimeout(() => setAttempt((n) => n + 1), 2000);
          return;
        }
        hasRelay.current = true;
        earlier.current = r.earlier;
        // Same camera relay keeps playing across state changes: only a new round (or a retry) reloads it.
        setRelay((cur) => (cur && cur.key === attempt && cur.url.split('?')[0] === r.playlistUrl.split('?')[0] ? cur : { url: r.playlistUrl, key: attempt, segS: (r.segmentMs ?? 5000) / 1000 }));
      },
      () => {
        if (live && !hasRelay.current) timer = setTimeout(() => setAttempt((n) => n + 1), 2000);
      },
    );
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [gw, round.gameRoundId, round.state, attempt]);

  useEffect(() => {
    segs.current.clear();
    hits.current.clear();
  }, [round.gameRoundId]);

  // Player: hls.js on our relay; every loaded segment brings its sidecar (boxes + crossings).
  useEffect(() => {
    const v = video.current;
    if (!v || !relay) return;
    let dead = false;
    let hls: import('hls.js').default | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const retry = () => {
      if (dead) return;
      setState('RETRYING');
      hasRelay.current = false;
      clearTimeout(timer);
      timer = setTimeout(() => setAttempt((n) => n + 1), 1500);
    };
    void import('hls.js').then(({ default: Hls }) => {
      if (dead) return;
      if (!Hls.isSupported()) {
        v.src = relay.url;
        return;
      }
      // Stay one segment plus a cushion behind the newest counted segment. Counted segments do not arrive
      // like clockwork (source cadence, our poll, counting time): measured jitter is 2-3 s, and with a 1 s
      // cushion the picture froze for about 15 s of every round. Smooth beats two seconds closer to live.
      hls = new Hls({ lowLatencyMode: true, liveSyncDuration: relay.segS + CUSHION_S, liveMaxLatencyDuration: relay.segS * 2 + CUSHION_S + 6, maxLiveSyncPlaybackRate: 1.1, maxBufferLength: 20, manifestLoadingMaxRetry: 3, fragLoadingMaxRetry: 3 });
      hlsRef.current = hls;
      hls.on(Hls.Events.FRAG_LOADED, (_e, d) => {
        const f = d.frag;
        if (typeof f.sn !== 'number') return;
        setState('OK');
        const sn = f.sn;
        void fetch(f.url.replace(/\/(\d+)\.(ts|m4s)\?/, '/$1.json?'))
          .then((r) => (r.ok ? (r.json() as Promise<Sidecar>) : null))
          .then((side) => {
            if (dead || !side) return;
            segs.current.set(sn, { ...side, frag: f });
            const r = roundRef.current.manifest;
            for (const c of side.crossings)
              hits.current.set(c.eventId, { eventId: c.eventId, seq: c.seq, offsetMs: c.offsetMs, fallbackT: f.start, wallMs: c.wallMs, at: c.at, counted: c.direction === r.line.countedDirection && (r.includedClasses as string[]).includes(c.vehicleClass) });
            // Keep about two minutes (a whole window) of segments and crossings.
            for (const [k, s] of segs.current) if (s.startMs < side.startMs - 130_000) segs.current.delete(k);
            for (const [k, h] of hits.current) if (h.wallMs < side.startMs - 130_000) hits.current.delete(k);
          })
          .catch(() => undefined);
      });
      hls.on(Hls.Events.ERROR, (_e, d) => {
        if (!d.fatal) return;
        if (d.type === Hls.ErrorTypes.MEDIA_ERROR) hls?.recoverMediaError();
        else retry();
      });
      hls.on(Hls.Events.MANIFEST_PARSED, () => void v.play().catch(() => undefined));
      hls.loadSource(relay.url);
      hls.attachMedia(v);
    });
    return () => {
      dead = true;
      clearTimeout(timer);
      hls?.destroy();
      hlsRef.current = null;
    };
  }, [relay]);

  // Draw loop: boxes of the frame on screen, the line, and the "+1" of each counted vehicle.
  useEffect(() => {
    let raf = 0;
    let lastCount: number | null = -1;
    let lastPhase: number | null = null;
    let lastTick = 0;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      const v = video.current;
      const cv = canvas.current;
      if (!v || !cv) return;
      const w = v.videoWidth;
      const h = v.videoHeight;
      if (!w || !h) return;
      if (cv.width !== w || cv.height !== h) {
        cv.width = w;
        cv.height = h;
      }
      const g = cv.getContext('2d');
      if (!g) return;
      g.clearRect(0, 0, w, h);
      const man = roundRef.current.manifest;
      const px = Math.max(2, Math.round(w / 420));
      // Capture time of the picture on screen, from the player itself (our playlist carries each segment's
      // capture time): boxes and crossings are placed on that same clock.
      const wallMs = hlsRef.current?.playingDate?.getTime() ?? null;
      let seg: Seg | null = null;
      if (wallMs !== null) for (const s of segs.current.values()) if (wallMs >= s.startMs && wallMs < s.startMs + s.durationMs) seg = s;
      const now = wallMs !== null ? wallMs / 1000 : 0;
      const timeOf = (h: Hit) => h.wallMs / 1000;
      const startMs = Date.parse(man.timing.captureStartAt);
      const endMs = Date.parse(man.timing.captureEndAt);
      const inWindow = wallMs !== null && wallMs >= startMs && wallMs < endMs;

      // Counting gate (locked in the manifest): a yellow approach zone in front of the line with chevrons in
      // the counted direction, and the counting line itself as the bold far edge. Only the line counts; the
      // zone shows where vehicles come from and which way counts. Slightly dimmer outside the window.
      // The line flashes white for a moment after each counted vehicle.
      let flash = 0;
      for (const hit of hits.current.values()) {
        const ht = timeOf(hit);
        if (hit.counted && hit.wallMs >= startMs && hit.wallMs < endMs && now >= ht && now - ht < 0.35) flash = Math.max(flash, 1 - (now - ht) / 0.35);
      }
      drawGate(g, man.line, w, h, { px, now, dim: !inWindow, flash, still: cbs.current.reducedMotion });

      // Track boxes of the frame on screen: one colour per tracked vehicle.
      if (seg) {
        const rel = wallMs! - seg.startMs;
        let fr: Box[] | null = null;
        for (const f of seg.frames) {
          if (f[0] > rel) break;
          fr = f[1];
        }
        // Corner brackets around each tracked vehicle (one colour per vehicle).
        g.lineWidth = px * 1.4;
        for (const b of fr ?? []) {
          const x1 = b[1] * w;
          const y1 = b[2] * h;
          const x2 = b[3] * w;
          const y2 = b[4] * h;
          const k = Math.max(px * 3, Math.min(x2 - x1, y2 - y1) * 0.28);
          g.strokeStyle = colour(b[0]);
          g.beginPath();
          for (const [cx, cy, sx, sy] of [
            [x1, y1, 1, 1],
            [x2, y1, -1, 1],
            [x1, y2, 1, -1],
            [x2, y2, -1, -1],
          ] as const) {
            g.moveTo(cx + sx * k, cy);
            g.lineTo(cx, cy);
            g.lineTo(cx, cy + sy * k);
          }
          g.stroke();
        }
      }

      // "+1" where a vehicle was counted, rising and fading.
      const font = Math.max(16, Math.round(h / 16));
      g.font = `800 ${font}px system-ui, sans-serif`;
      g.textAlign = 'center';
      let count = 0;
      // Counted before the first segment this client has seen (late joiner): known from the server.
      let firstMs = Infinity;
      for (const x of segs.current.values()) firstMs = Math.min(firstMs, x.startMs);
      for (const e of earlier.current) if (e < firstMs && (wallMs === null || e <= wallMs)) count++;
      for (const hit of hits.current.values()) {
        const inRound = hit.counted && hit.wallMs >= startMs && hit.wallMs < endMs;
        const ht = timeOf(hit);
        if (inRound && wallMs !== null && now >= ht) count++;
        const age = now - ht;
        // "+1" only for what counts: inside this round's window.
        if (!hit.at || age < 0 || age > POP_S || !inRound) continue;
        const k = age / POP_S;
        g.globalAlpha = 1 - k;
        const x = hit.at[0] * w;
        const y = hit.at[1] * h - font * (0.4 + 1.4 * k);
        g.lineWidth = Math.max(3, font / 6);
        g.strokeStyle = 'rgba(0,0,0,0.85)';
        g.strokeText('+1', x, y);
        g.fillStyle = '#bef264';
        g.fillText('+1', x, y);
        g.globalAlpha = 1;
      }

      // The count follows the picture while the window is (or has been) on screen.
      const shown = wallMs !== null && wallMs >= startMs ? count : null;
      // Sound follows the picture: the counted minute starts / a vehicle counts / the minute ends.
      if (cbs.current.soundOn && wallMs !== null) {
        const phase = wallMs < startMs ? 0 : wallMs < endMs ? 1 : 2;
        if (lastPhase !== null && phase !== lastPhase) {
          if (phase === 1) sounds.roundStart();
          if (phase === 2 && lastPhase === 1) sounds.roundEnd();
        }
        if (phase === 1 && shown !== null && lastCount !== null && lastCount !== -1 && shown > lastCount) sounds.plus(shown);
        lastPhase = phase;
      }
      if (shown !== lastCount) {
        lastCount = shown;
        cbs.current.onLiveCount?.(shown);
      }
      const t0 = performance.now();
      if (t0 - lastTick > 250) {
        lastTick = t0;
        const b = wallMs !== null ? Math.max(0, gw.serverNowMs() - wallMs) : null;
        const left = wallMs !== null && wallMs < startMs ? Math.ceil((startMs - wallMs) / 1000) : null;
        setStartsIn((cur) => (cur === left ? cur : left));
        const late = gw.serverNowMs() > endMs + 50_000;
        setStale((cur) => (cur === late ? cur : late));
        cbs.current.onPicture?.({ wallMs, behindMs: b });
        if (wallMs !== null && wallMs >= endMs) setEndedRound(roundRef.current.gameRoundId);
      }
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      cbs.current.onLiveCount?.(null);
      cbs.current.onPicture?.({ wallMs: null, behindMs: null });
    };
  }, [gw]);

  const status = round.state === 'VOIDED' || round.state === 'VOIDING' ? t('live.status.voided') : state === 'RETRYING' ? t('live.status.retrying') : state === 'LOADING' ? t('relay.connecting') : null;
  // While the round runs: the country only. When its window has ended on screen (or it was voided): the place.
  const voided = ['VOIDED', 'VOIDING'].includes(round.state) || outcome?.kind === 'VOIDED';
  const decided = ['RESULT_FINAL', 'SETTLING', 'SETTLED'].includes(round.state) && outcome?.kind === 'RESULT' && outcome.finalCount !== null;
  // The window has ended on screen — or the picture stalled and the server clock is well past it.
  const over = voided || ((ended || stale) && decided);
  const country = cam.country ? cam.country.toUpperCase() : '';
  const videoOpacity = intro ? intro.videoOpacity : 1;
  const sum = summary?.id === round.gameRoundId ? summary : null;
  return (
    <section className="live-stage" data-testid="live-stage" data-phase={intro ? intro.phase : over ? 'OUTRO' : 'RELAY'} data-relay={state} aria-label={t('live.stage')}>
      <video ref={video} className="live-video" muted playsInline autoPlay style={{ opacity: videoOpacity }} data-testid="live-video" aria-label={t('live.video')} />
      <canvas ref={canvas} className="live-boxes" style={{ opacity: videoOpacity }} data-testid="live-boxes" aria-hidden />
      {intro && intro.videoOpacity < 1 && (
        <div className="live-globe" style={{ opacity: 1 - intro.videoOpacity }} data-testid="live-globe">
          <Globe center={intro.center} zoom={intro.zoom} target={lat !== null && lon !== null ? { lon, lat } : null} markerOpacity={0} countryOpacity={intro.countryOpacity} pulse={0} label={country} />
        </div>
      )}
      {intro && intro.flash > 0 && <div className="live-flash" style={{ opacity: intro.flash }} aria-hidden />}
      {intro && intro.infoOpacity > 0 && (
        <div className="live-card" style={{ opacity: intro.infoOpacity }} data-testid="live-card">
          <div className="live-title">{country}</div>
          <div className="live-badge">{t('relay.nextCamera')}</div>
        </div>
      )}
      {!intro && !over && (
        <div className="live-tag" data-testid="live-tag">
          <strong>{country}</strong>
          <span className="live-badge">{t('relay.badge')}</span>
        </div>
      )}
      {/* Betting is closed and the counted minute has not started on screen yet. */}
      {!intro && !over && round.state !== 'BETTING_OPEN' && round.state !== 'SCHEDULED' && startsIn !== null && (
        <div className={`live-starting ${startsIn <= 10 ? 'counting-down' : ''}`} role="status" data-testid="live-starting">
          <span>{t('relay.starting')}</span>
          {/* The last ten seconds are counted down large, in the middle of the picture. */}
          {startsIn <= 10 && (
            <strong key={startsIn} className="tabular">
              {startsIn}
            </strong>
          )}
        </div>
      )}
      {over && (
        <div className="live-outro" role="status" data-testid="live-outro">
          <div className="live-outro-place">
            <strong>{lines.title.length ? lines.title.join(', ') : country}</strong>
            {lines.detail.length > 0 && <span>{lines.detail.join(' · ')}</span>}
          </div>
          {voided ? (
            <p className="live-outro-main">{t('outro.voided')}</p>
          ) : (
            <>
              <div className="live-outro-count">
                <span>{t('outro.count')}</span>
                <strong className="tabular">{outcome!.finalCount}</strong>
              </div>
              <p className="live-outro-main">{t('outro.winning', { sel: outcome!.winning.map((w) => t(`sel.${w as GameSelection}` as MessageKey)).join(', ') })}</p>
              {sum && (
                <p className="live-outro-stats" data-testid="live-outro-stats">
                  {sum.players === 0 ? t('outro.noPlayers') : `${t('outro.players', { winners: sum.winners, players: sum.players })} · ${t('outro.paid', { amount: fmtAtomic(BigInt(sum.totalPaid.amountAtomic), sum.totalPaid.scale, sum.totalPaid.currency, intl) })}`}
                </p>
              )}
            </>
          )}
        </div>
      )}
      {status && !intro && !over && (
        <p className="live-status" role="status" data-testid="live-status">
          {status}
        </p>
      )}
    </section>
  );
}
